import { useState, useEffect } from "react";
import { mergePlanTemplateDto, PLAN_ORDER } from "../../../api/usePlanTemplatesMerged";
import { fetchPlanTemplates, putPlanTemplate, type PlanTemplateDto } from "../../../api/planTemplates";
import { useTenants } from "../../../api/useTenants";
import type { TenantPlan } from "../../../api/types";
import { getUserFacingMessage, reportError } from "../../../api/errors";

interface PlanRow {
  key: TenantPlan;
  label: string;
  color: string;
  bgColor: string;
  price: number;
  maxMembers: number;
  maxAdmins: number;
  storage: number;
  features: string[];
}

function buildPlansFromApi(rows: PlanTemplateDto[]): PlanRow[] {
  return PLAN_ORDER.map((k) => {
    const row = rows.find((r) => r.plan === k);
    const m = mergePlanTemplateDto(k, row);
    return { key: k, ...m };
  });
}

export default function PlanManager() {
  const { tenants } = useTenants();
  const [plans, setPlans] = useState<PlanRow[]>([]);
  const [templatesLoaded, setTemplatesLoaded] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [editingPlan, setEditingPlan] = useState<string | null>(null);
  const [editPrice, setEditPrice] = useState(0);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newFeatureInputs, setNewFeatureInputs] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    fetchPlanTemplates()
      .then((rows) => {
        if (cancelled) return;
        setPlans(buildPlansFromApi(rows));
        setLoadError(null);
        setTemplatesLoaded(true);
      })
      .catch((e) => {
        if (cancelled) return;
        setLoadError(getUserFacingMessage(e, "套餐模板加载失败，请刷新页面。"));
        setPlans(PLAN_ORDER.map((k) => ({ key: k, ...mergePlanTemplateDto(k, undefined) })));
        setTemplatesLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const sortedPlans = PLAN_ORDER.map((k) => plans.find((p) => p.key === k)).filter(Boolean) as PlanRow[];

  const handleSavePrice = (key: string) => {
    const plan = plans.find((p) => p.key === key);
    if (!plan) return;
    setSaving(true);
    void putPlanTemplate(key as TenantPlan, {
      label: plan.label,
      priceAnnual: editPrice,
      features: plan.features,
    })
      .then((row) => {
        setPlans((p) =>
          p.map((pl) =>
            pl.key === key
              ? {
                  ...pl,
                  price: Number(row.priceAnnual),
                  label: row.label,
                  features: [...row.features],
                }
              : pl,
          ),
        );
        setEditingPlan(null);
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
      })
      .catch((e) => {
        reportError(e);
      })
      .finally(() => setSaving(false));
  };

  const persistAllPlans = () => {
    setSaving(true);
    void Promise.all(
      sortedPlans.map((p) =>
        putPlanTemplate(p.key, { label: p.label, priceAnnual: p.price, features: p.features }),
      ),
    )
      .then((rows) => {
        setPlans(buildPlansFromApi(rows));
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
      })
      .catch((e) => {
        reportError(e);
      })
      .finally(() => setSaving(false));
  };

  const handleToggleFeature = (planKey: string, feat: string) => {
    setPlans((p) =>
      p.map((plan) => {
        if (plan.key !== planKey) return plan;
        const hasIt = plan.features.includes(feat);
        return {
          ...plan,
          features: hasIt ? plan.features.filter((f) => f !== feat) : [...plan.features, feat],
        };
      }),
    );
  };

  const handleAddFeature = (planKey: string) => {
    const input = newFeatureInputs[planKey]?.trim();
    if (!input) return;
    setPlans((p) =>
      p.map((plan) =>
        plan.key === planKey && !plan.features.includes(input)
          ? { ...plan, features: [...plan.features, input] }
          : plan,
      ),
    );
    setNewFeatureInputs((p) => ({ ...p, [planKey]: "" }));
  };

  const handleRemoveFeature = (planKey: string, feat: string) => {
    setPlans((p) =>
      p.map((plan) =>
        plan.key === planKey ? { ...plan, features: plan.features.filter((f) => f !== feat) } : plan,
      ),
    );
  };

  const allFeatures = [
    "会员管理", "内容发布", "活动管理", "投票管理",
    "消息通知", "数据报表", "系统设置", "API接入",
    "定制开发", "多端支持", "专属客服", "无限会员", "全部功能",
    "基础活动", "数据总览",
  ];

  const totalRevenue = sortedPlans.reduce((sum, plan) => {
    const tenantCount = tenants.filter((t) => t.plan === plan.key).length;
    return sum + plan.price * tenantCount;
  }, 0);

  if (!templatesLoaded) {
    return (
      <div className="text-sm text-gray-500 py-12 text-center">
        正在加载套餐配置…
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-lg font-bold text-gray-800">套餐管理</h2>
          <p className="text-xs text-gray-400 mt-0.5">配置各套餐价格和功能权限开关（数据持久化至数据库）</p>
          {loadError && (
            <p className="text-xs text-amber-600 mt-1">接口不可用，已回退本地默认：{loadError}</p>
          )}
        </div>
        <button
          type="button"
          disabled={saving}
          onClick={persistAllPlans}
          className={`flex items-center gap-2 px-4 py-2 text-sm rounded-xl cursor-pointer transition-all whitespace-nowrap disabled:opacity-50 ${
            saved ? "bg-green-500 text-white" : "bg-amber-500 text-white hover:bg-amber-400"
          }`}
        >
          <i className={saved ? "ri-check-line" : "ri-save-line"}></i>
          {saved ? "已保存" : "保存配置"}
        </button>
      </div>

      {/* Plan Cards Grid */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        {sortedPlans.map((plan) => {
          const tenantCount = tenants.filter((t) => t.plan === plan.key).length;
          return (
            <div
              key={plan.key}
              className={`bg-white rounded-2xl overflow-hidden border-2 ${
                plan.key === "standard" ? "border-amber-300" : "border-transparent"
              }`}
            >
              {plan.key === "standard" && (
                <div className="bg-amber-500 text-white text-xs text-center py-1.5 font-medium">
                  主力套餐
                </div>
              )}
              <div className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${plan.bgColor} ${plan.color}`}>
                      {plan.label}
                    </span>
                    <p className="text-xs text-gray-400 mt-1.5">{tenantCount} 个租户在用</p>
                  </div>
                </div>

                {/* Price */}
                <div className="mb-4">
                  {editingPlan === plan.key ? (
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500 text-sm">¥</span>
                      <input
                        type="number"
                        value={editPrice}
                        onChange={(e) => setEditPrice(parseInt(e.target.value, 10) || 0)}
                        className="flex-1 border border-amber-300 rounded-lg px-2 py-1 text-sm focus:outline-none w-24"
                      />
                      <button type="button" disabled={saving} onClick={() => handleSavePrice(plan.key)} className="text-xs bg-amber-500 text-white px-2 py-1 rounded-lg cursor-pointer whitespace-nowrap disabled:opacity-50">保存</button>
                      <button type="button" onClick={() => setEditingPlan(null)} className="text-xs text-gray-500 cursor-pointer">取消</button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="flex items-baseline gap-1 cursor-pointer group bg-transparent border-0 p-0 text-left w-full"
                      onClick={() => { setEditingPlan(plan.key); setEditPrice(plan.price); }}
                    >
                      {plan.price > 0 ? (
                        <>
                          <span className="text-2xl font-bold text-gray-900">¥{plan.price.toLocaleString()}</span>
                          <span className="text-xs text-gray-400">/年</span>
                        </>
                      ) : (
                        <span className="text-2xl font-bold text-gray-500">免费</span>
                      )}
                      <i className="ri-edit-line text-xs text-gray-300 group-hover:text-amber-500 ml-1 transition-colors"></i>
                    </button>
                  )}
                </div>

                {/* Limits */}
                <div className="space-y-1.5 mb-4 text-xs text-gray-500">
                  <div className="flex justify-between">
                    <span>最大会员</span>
                    <span className="font-medium text-gray-700">
                      {plan.maxMembers >= 999999 ? "无限制" : plan.maxMembers.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>管理账号</span>
                    <span className="font-medium text-gray-700">
                      {plan.maxAdmins >= 999 ? "无限制" : plan.maxAdmins} 个
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>存储空间</span>
                    <span className="font-medium text-gray-700">
                      {plan.storage >= 10000 ? "10GB+" : `${plan.storage}MB`}
                    </span>
                  </div>
                </div>

                {/* Features */}
                <div>
                  <p className="text-xs font-semibold text-gray-500 mb-2">功能开关</p>
                  <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
                    {allFeatures.map((feat) => {
                      const enabled = plan.features.includes(feat) || plan.features.includes("全部功能");
                      return (
                        <label key={`${plan.key}-${feat}`} className="flex items-center gap-2 cursor-pointer select-none group">
                          <input
                            type="checkbox"
                            checked={enabled}
                            onChange={() => handleToggleFeature(plan.key, feat)}
                            className="w-3.5 h-3.5 rounded accent-amber-500"
                          />
                          <span className={`text-xs ${enabled ? "text-gray-700" : "text-gray-400"}`}>{feat}</span>
                        </label>
                      );
                    })}
                    {plan.features
                      .filter((f) => !allFeatures.includes(f))
                      .map((feat) => (
                        <div key={`${plan.key}-c-${feat}`} className="flex items-center justify-between gap-2">
                          <span className="text-xs text-gray-700">{feat}</span>
                          <button type="button" className="text-[10px] text-red-500 cursor-pointer" onClick={() => handleRemoveFeature(plan.key, feat)}>移除</button>
                        </div>
                      ))}
                  </div>

                  <div className="flex gap-1 mt-3">
                    <input
                      type="text"
                      value={newFeatureInputs[plan.key] || ""}
                      onChange={(e) => setNewFeatureInputs((p) => ({ ...p, [plan.key]: e.target.value }))}
                      onKeyDown={(e) => e.key === "Enter" && handleAddFeature(plan.key)}
                      placeholder="自定义功能..."
                      className="flex-1 border border-gray-200 rounded-lg px-2 py-1 text-xs focus:outline-none focus:border-amber-400"
                    />
                    <button
                      type="button"
                      onClick={() => handleAddFeature(plan.key)}
                      className="w-7 h-7 flex items-center justify-center bg-gray-100 rounded-lg hover:bg-amber-50 hover:text-amber-600 cursor-pointer transition-colors text-gray-500 flex-shrink-0"
                    >
                      <i className="ri-add-line text-sm"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Billing Overview */}
      <div className="bg-white rounded-2xl p-5">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">收费概览</h3>
        <div className="grid grid-cols-4 gap-4">
          {sortedPlans.map((plan) => {
            const tenantCount = tenants.filter((t) => t.plan === plan.key).length;
            const revenue = plan.price * tenantCount;
            return (
              <div key={plan.key} className={`p-4 rounded-xl ${plan.bgColor}`}>
                <p className={`text-xs font-medium ${plan.color} mb-1`}>{plan.label}</p>
                <p className="text-lg font-bold text-gray-800">
                  {revenue > 0 ? `¥${revenue.toLocaleString()}` : "-"}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">{tenantCount} 个租户 / 年</p>
              </div>
            );
          })}
        </div>
        <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
          <span className="text-sm text-gray-600 font-medium">平台年收入合计</span>
          <span className="text-xl font-bold text-gray-900">¥{totalRevenue.toLocaleString()}</span>
        </div>
      </div>
    </div>
  );
}
